/**
 * As contas e as palavras da tela de pessoas e documentos. Fora do componente,
 * porque é o que dá para provar sem montar React.
 *
 * NADA AQUI CALCULA VALIDADE. O estado do documento (`vencido`, `vence_logo`,
 * `valido`) vem da view `pess.validade_do_documento`, porque o aviso em dias é
 * parâmetro de cada tipo: uma segunda conta aqui divergiria na primeira vez que
 * alguém trocasse o número, e aí a tela e o impedimento discordariam sobre a
 * mesma CNH.
 */
import { faltaNoMotivo } from '@/lib/motivo';

import type {
  Aptidao,
  FuncaoDaPessoa,
  DocumentoNaLista,
  EstadoDoDocumento,
  Exigencia,
  Impedimento,
  MotivoDoImpedimento,
} from './api';

export function estadoEscrito(estado: EstadoDoDocumento): string {
  if (estado === 'vencido') return 'Vencido';
  if (estado === 'vence_logo') return 'Vence logo';
  if (estado === 'sem_data') return 'Sem validade informada';
  if (estado === 'sem_validade') return 'Não vence';
  return 'Válido';
}

/**
 * O motivo escrito, e os três não se misturam.
 *
 * Eles impedem igual e se resolvem diferente: ausente é ir buscar o documento,
 * vencido é renovar, sem data é corrigir o cadastro. Dizer "irregular" para os
 * três faria quem resolve procurar a coisa errada.
 */
export function motivoEscrito(motivo: MotivoDoImpedimento): string {
  if (motivo === 'ausente') return 'Nunca cadastrado';
  if (motivo === 'vencido') return 'Vencido';
  return 'Sem validade informada';
}

/** O que fazer com cada motivo. É a frase que torna a lista acionável. */
export function oQueFazer(motivo: MotivoDoImpedimento): string {
  if (motivo === 'ausente') return 'Buscar o documento e cadastrá-lo';
  if (motivo === 'vencido') return 'Renovar e gravar a validade nova';
  return 'Corrigir o cadastro com a data de validade';
}

export interface TotaisDePessoas {
  /** Impedimentos obrigatórios em aberto. É o número que impede a operação. */
  readonly impedimentosObrigatorios: number;
  /** Máquinas distintas com algum impedimento obrigatório. */
  readonly maquinasImpedidas: number;
  /** Pessoas distintas com algum impedimento obrigatório. */
  readonly pessoasImpedidas: number;
  /** Documentos vencidos, em toda a base — não só nos alocados. */
  readonly vencidos: number;
  /** Documentos que vencem dentro do aviso do tipo. */
  readonly venceLogo: number;
}

/**
 * Os totais da faixa.
 *
 * `maquinasImpedidas` e `pessoasImpedidas` contam ALVOS DISTINTOS, não linhas:
 * a mesma máquina pode faltar três documentos, e contá-la três vezes faria o
 * número parecer uma frota parada quando é uma máquina com três pendências.
 *
 * E eles contam só o OBRIGATÓRIO: o requisito recomendado entra na lista para
 * ser visto, mas não impede a operação, e somá-lo ao alerta faria a tela
 * mostrar um impedimento que não impede.
 */
export function totaisDePessoas(
  impedimentos: readonly Impedimento[],
  documentos: readonly DocumentoNaLista[],
): TotaisDePessoas {
  const obrigatorios = impedimentos.filter((i) => i.obrigatorio);
  return {
    impedimentosObrigatorios: obrigatorios.length,
    maquinasImpedidas: new Set(obrigatorios.filter((i) => i.sobre === 'equipamento').map((i) => i.alvoId)).size,
    pessoasImpedidas: new Set(obrigatorios.filter((i) => i.sobre === 'pessoa').map((i) => i.alvoId)).size,
    vencidos: documentos.filter((d) => d.estado === 'vencido').length,
    venceLogo: documentos.filter((d) => d.estado === 'vence_logo').length,
  };
}

export type EstadoDaFaixa = 'carregando' | 'falhou' | 'pronta';

export function estadoDaFaixa(consulta: { readonly data: unknown; readonly isError: boolean }): EstadoDaFaixa {
  if (consulta.isError) return 'falhou';
  return consulta.data === undefined ? 'carregando' : 'pronta';
}

/**
 * De quem é o impedimento, escrito por extenso.
 *
 * "ESC-001, na Frente 51" é diferente de "ESC-001": o mesmo documento vencido
 * impede em uma obra e não em outra, porque a exigência é do contrato ou do
 * local. Sem o lugar, a linha não diz onde o problema aparece.
 */
export function ondeImpede(impedimento: Impedimento): string {
  const lugar = impedimento.obra ?? impedimento.contrato;
  if (lugar === null) return impedimento.alvo;
  return `${impedimento.alvo} · ${lugar}`;
}

/**
 * A autorização está vigente?
 *
 * Encerrar fecha a vigência em vez de apagar a linha — a história de quem
 * estava autorizado no dia do acidente é exatamente o que alguém vai
 * perguntar. Então a lista traz as encerradas, e é esta função que as separa.
 */
export function estaVigente(aptidao: Pick<Aptidao, 'ate'>): boolean {
  return aptidao.ate === null;
}

/** O que a autorização cobre, escrito: modelo ou configuração, nunca máquina. */
export function aptidaoEscrita(aptidao: Aptidao): string {
  const alvo = aptidao.configuracao ?? aptidao.modelo;
  if (alvo === null) return 'Alvo não informado';
  return aptidao.configuracao === null ? `Modelo ${alvo}` : `Configuração ${alvo}`;
}

/**
 * A exigência foi validada com o cliente?
 *
 * A ferramenta 3 do v6 pede a lista "validada pelo responsável", e a razão é
 * direta: sem alguém ter assinado embaixo, "o cliente exige" é boato — e um
 * impedimento apoiado em boato é o que ensina a ignorar a lista.
 */
export function exigenciaPendenteDeValidacao(exigencias: readonly Exigencia[]): readonly Exigencia[] {
  return exigencias.filter((x) => x.validadoEm === null);
}

/** O alvo da exigência, escrito: contrato ou obra. */
export function alvoDaExigencia(exigencia: Exigencia): string {
  if (exigencia.obra !== null) return `Obra ${exigencia.obra}`;
  if (exigencia.contrato !== null) return `Contrato ${exigencia.contrato}`;
  return 'Alvo não informado';
}

/* ------------------------------------------------- a função da pessoa (0095) */

/** A função que vale hoje: a vigência que contém o dia. */
export function funcaoVigente(v: Pick<FuncaoDaPessoa, 'desde' | 'ate'>, hoje: string): boolean {
  return v.desde <= hoje && (v.ate === null || v.ate > hoje);
}

export interface ValoresDaFuncaoDaPessoa {
  readonly usuarioId: string;
  readonly funcaoId: string;
  readonly desde: string;
  readonly motivo: string;
}

/** O que falta, por campo. */
export function faltaNaFuncaoDaPessoa(v: ValoresDaFuncaoDaPessoa): Partial<Record<keyof ValoresDaFuncaoDaPessoa, string>> {
  const falta: Partial<Record<keyof ValoresDaFuncaoDaPessoa, string>> = {};
  if (v.usuarioId === '') falta.usuarioId = 'Escolha a pessoa.';
  if (v.funcaoId === '') falta.funcaoId = 'Escolha a função.';
  if (!/^\d{4}-\d{2}-\d{2}$/.test(v.desde)) falta.desde = 'Informe a data em que a pessoa passou a exercer a função.';
  const motivo = faltaNoMotivo(v.motivo);
  if (motivo) falta.motivo = motivo;
  return falta;
}

export interface ValoresDaFuncao {
  readonly codigo: string;
  readonly nome: string;
}

export function faltaNaFuncao(v: ValoresDaFuncao): Partial<Record<keyof ValoresDaFuncao, string>> {
  const falta: Partial<Record<keyof ValoresDaFuncao, string>> = {};
  if (v.codigo.trim() === '') falta.codigo = 'Informe o código: curto e único, como OPESC ou MEC.';
  if (v.nome.trim() === '') falta.nome = 'Informe o nome da função, como Operador de escavadeira.';
  return falta;
}

/**
 * A VALIDADE DIGITADA NA TELA, NO FORMATO DE TRANSPORTE.
 *
 * Achado dos robôs no QAS (20/10/2026): a validade do CRLV em `dd/mm/aaaa`
 * deixava "Gravar documento" habilitado, e o servidor recusava com "Use o
 * formato aaaa-mm-dd" — a tela pedia o formato de máquina. Na tela a data é
 * `dd/mm/aaaa` (p.61); `aaaa-mm-dd` só no transporte, e a conversão é aqui.
 *
 * Aceita `dd/mm/aaaa` (e `d/m/aaaa`) e, por tolerância, o próprio
 * `aaaa-mm-dd`. Devolve `null` para o que não é um dia do calendário (31/02,
 * 2026-13-01, texto). O dia é lido do texto, nunca por `new Date(texto)`, que
 * é meia-noite UTC e no fuso da operação ainda é o dia anterior.
 */
export function validadeParaEnvio(texto: string): string | null {
  const t = texto.trim();
  const br = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(t);
  const iso = /^(\d{4})-(\d{2})-(\d{2})$/.exec(t);
  const [ano, mes, dia] = br ? [Number(br[3]), Number(br[2]), Number(br[1])] : iso ? [Number(iso[1]), Number(iso[2]), Number(iso[3])] : [NaN, NaN, NaN];
  if (!Number.isInteger(ano) || mes < 1 || mes > 12 || dia < 1) return null;
  const diasNoMes = new Date(Date.UTC(ano, mes, 0)).getUTCDate();
  if (dia > diasNoMes) return null;
  return `${String(ano).padStart(4, '0')}-${String(mes).padStart(2, '0')}-${String(dia).padStart(2, '0')}`;
}

/** O erro do campo "Válido até", ao sair dele: diz o que fazer (p.23). `null` quando está bom. */
export function erroDaValidade(texto: string, obrigatoria: boolean): string | null {
  if (texto.trim() === '') return obrigatoria ? 'Informe até quando o documento vale, no formato dd/mm/aaaa.' : null;
  return validadeParaEnvio(texto) === null ? 'Escreva a data no formato dd/mm/aaaa, por exemplo 30/10/2026.' : null;
}

/**
 * O DOCUMENTO QUE JÁ EXISTE para o tipo e o dono escolhidos (31/10/2026). A
 * janela diz "Cadastrar ou renovar", e renovar é gravar a data nova no mesmo
 * documento: com ele achado, a janela já vem com o número e o responsável
 * dele, e quem renova só escreve a validade nova.
 */
export function documentoAtual(
  documentos: readonly DocumentoNaLista[],
  escolha: { readonly tipoId: string; readonly usuarioId: string; readonly equipamentoId: string },
): DocumentoNaLista | null {
  if (escolha.tipoId === '' || (escolha.usuarioId === '' && escolha.equipamentoId === '')) return null;
  return (
    documentos.find(
      (d) =>
        d.tipoId === escolha.tipoId &&
        (escolha.usuarioId !== '' ? d.usuarioId === escolha.usuarioId : d.equipamentoId === escolha.equipamentoId),
    ) ?? null
  );
}

/**
 * O RESPONSÁVEL PROPOSTO para o documento novo: o de quem já cuida do documento
 * que está sendo renovado; sem ele, quem está cadastrando, se está na lista.
 */
export function responsavelProposto(
  atual: DocumentoNaLista | null,
  eu: string | null,
  pessoas: readonly { readonly valor: string }[],
): { readonly id: string; readonly origem: 'documento' | 'voce' } | null {
  if (atual?.responsavelId) return { id: atual.responsavelId, origem: 'documento' };
  if (eu !== null && pessoas.some((p) => p.valor === eu)) return { id: eu, origem: 'voce' };
  return null;
}
